import type { CSSProperties, ReactNode } from 'react';
import { MarcaLogin } from '../layout/Logo';

/**
 * Aviso de Privacidade — rota PÚBLICA, e é a razão de ela existir.
 *
 * Este endereço vai dentro da mensagem de consentimento que o participante lê no
 * WhatsApp (`{{link_privacidade}}`). Quem abre veio de uma conversa, no celular,
 * antes de ter autorizado qualquer coisa — então não pode haver login, não pode
 * haver cookie de sessão, e o texto tem de ser legível numa tela pequena.
 *
 * O conteúdo não é enfeite jurídico: ele é o "detalhamento" que o próprio texto
 * do consentimento promete ("Finalidades, compartilhamentos, prazos e seus
 * direitos: …"). Cada promessa feita lá tem de ser respondida aqui, ou o
 * consentimento deixa de ser informado — e consentimento não informado é o mesmo
 * que consentimento nenhum.
 */

/**
 * ⚠️ Esta caixa precisa EXISTIR antes de a jornada ir ao ar.
 *
 * Um canal de exercício de direitos que ninguém lê é pior do que não prometer
 * canal: a LGPD exige resposta em 15 dias, e o prazo corre a partir do pedido,
 * não a partir de alguém ter percebido o pedido.
 */
const EMAIL_ENCARREGADO = 'contato@materiaprimai.com.br';

const ATUALIZADO_EM = '17 de setembro de 2026';

export function Privacidade() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '32px 20px 64px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <MarcaLogin />

        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '28px 0 6px', color: 'var(--text)' }}>
          Aviso de Privacidade
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '0 0 28px' }}>
          Última atualização: {ATUALIZADO_EM}
        </p>

        <Bloco>
          Este aviso explica o que acontece com as suas informações quando você conversa com a
          MatériaPrimAi pelo WhatsApp durante um evento. Ele complementa a mensagem de
          autorização que você recebeu — o que está resumido lá está detalhado aqui.
        </Bloco>

        <Secao titulo="Quem trata os seus dados">
          <p>
            São dois, com papéis diferentes, e a distinção importa para você saber a quem
            recorrer:
          </p>
          <ul style={LISTA}>
            <li>
              <b>A empresa que organiza o evento</b> decide que o evento existe, quem é
              convidado e para que servem as conexões. É a <b>controladora</b> dos dados. O nome
              dela aparece na mensagem de autorização que você recebeu.
            </li>
            <li>
              <b>A MatériaPrimAi</b> opera a conversa, monta o perfil e sugere as conexões, sob
              instrução da organizadora. É a <b>operadora</b>.
            </li>
          </ul>
          <p>
            Você pode exercer seus direitos com qualquer um dos dois. Se procurar a
            MatériaPrimAi, encaminhamos à organizadora quando a decisão for dela.
          </p>
        </Secao>

        <Secao titulo="Qual é a base legal">
          <p>
            O seu <b>consentimento</b>, dado no WhatsApp, e nada além dele. Não usamos
            "legítimo interesse" para esta finalidade: como a conversa monta um perfil seu e o
            mostra a outras pessoas, a decisão precisa ser sua, explícita e reversível.
          </p>
          <p>
            Enquanto você não autoriza, nada é analisado. Se você recusar, sua inscrição no
            evento continua valendo normalmente — a experiência de conexões é opcional e
            separada dela.
          </p>
        </Secao>

        <Secao titulo="Quais dados usamos">
          <ul style={LISTA}>
            <li>
              <b>Cadastro:</b> nome, telefone, e — quando a organizadora os tiver — e-mail,
              cargo e empresa, vindos da sua inscrição no evento.
            </li>
            <li>
              <b>O que você envia na conversa:</b> respostas em texto, respostas em áudio, e o
              LinkedIn ou o currículo que você optar por mandar.
            </li>
            <li>
              <b>Registros técnicos:</b> data e hora das mensagens, status de entrega e leitura
              — o necessário para a conversa funcionar e para provar o que foi enviado a você.
            </li>
          </ul>
          <p>
            <b>Os áudios são transcritos e analisados</b> como se fossem texto. Dizemos isso
            aqui e na própria autorização porque é o ponto que costuma surpreender.
          </p>
          <p>
            Não pedimos, e pedimos que você não envie, dados sensíveis — saúde, biometria,
            religião, opinião política, filiação sindical, vida sexual, origem racial. Se algo
            assim aparecer espontaneamente na conversa, não será usado para montar o seu perfil
            nem para sugerir conexões.
          </p>
        </Secao>

        <Secao titulo="Para que usamos">
          <ul style={LISTA}>
            <li>Montar um perfil seu — interesses, objetivos e o que você busca no evento.</li>
            <li>
              Comparar esse perfil com o de outros participantes e identificar com quem a
              conversa tende a valer a pena.
            </li>
            <li>Enviar as sugestões de conexão a você, por este mesmo WhatsApp.</li>
            <li>
              Dar à organizadora números sobre o andamento — quantos aceitaram, quantos
              concluíram. Em forma de estatística, não de leitura da sua conversa.
            </li>
          </ul>
          <p>
            <b>Não</b> usamos suas informações para publicidade, não as vendemos e não as
            cedemos a terceiros para uso próprio deles.
          </p>
        </Secao>

        <Secao titulo="O que outras pessoas veem">
          <p>
            Quando sugerimos uma conexão entre você e outro participante, mostramos a essa
            pessoa <b>seu nome e um resumo do seu perfil</b> — não a sua conversa, não suas
            respostas na íntegra, não seu e-mail.
          </p>
          <p>
            <b>Seu telefone é caso à parte</b>, e a escolha foi sua na autorização:
          </p>
          <ul style={LISTA}>
            <li>
              <b>"Autorizo com telefone":</b> seu número vai junto com a indicação, e a pessoa
              pode falar com você diretamente.
            </li>
            <li>
              <b>"Autorizo sem telefone":</b> seu número não vai. A cada indicação nós
              perguntamos, e ele só é compartilhado se você disser sim <b>naquela</b> indicação.
            </li>
          </ul>
          <p>
            Você pode trocar de opção quando quiser, escrevendo na própria conversa. A troca
            vale daí em diante — o que já foi compartilhado antes não volta atrás, e é por isso
            que a escolha é pedida antes de qualquer indicação.
          </p>
        </Secao>

        <Secao titulo="Com quem compartilhamos">
          <ul style={LISTA}>
            <li>
              <b>Com a empresa organizadora do evento</b>, que é a controladora e para quem a
              experiência existe.
            </li>
            <li>
              <b>Com outros participantes</b>, apenas o que está descrito na seção acima.
            </li>
            <li>
              <b>Com fornecedores de tecnologia</b> que executam partes do serviço sob contrato
              e sem uso próprio dos dados: a Meta (WhatsApp Business), que entrega as mensagens;
              o provedor de inteligência artificial que transcreve áudios e analisa respostas; e
              o provedor de hospedagem onde os dados ficam guardados.
            </li>
          </ul>
          <p>
            Alguns desses fornecedores operam fora do Brasil, o que implica transferência
            internacional. Ela é feita com as garantias que a LGPD exige para essa hipótese.
          </p>
        </Secao>

        <Secao titulo="Por quanto tempo guardamos">
          <ul style={LISTA}>
            <li>
              <b>Conversa, perfil e indicações:</b> até <b>12 meses</b> após o fim do evento, e
              depois são apagados ou anonimizados.
            </li>
            <li>
              <b>O registro da sua autorização</b> — o texto exato que você leu, a opção que
              escolheu, a data: por <b>5 anos</b>. Esse registro é o que prova que pedimos antes
              de usar, e guardá-lo é o que permite responder se alguém questionar.
            </li>
            <li>
              <b>Pedido de saída:</b> guardamos o mínimo para não voltar a escrever para você.
              Apagar isso por completo faria você ser convidado de novo no evento seguinte.
            </li>
          </ul>
        </Secao>

        <Secao titulo="Seus direitos">
          <p>A LGPD garante que você pode, a qualquer momento e sem custo:</p>
          <ul style={LISTA}>
            <li>Confirmar que tratamos seus dados e acessar o que temos.</li>
            <li>Corrigir o que estiver errado, incompleto ou desatualizado.</li>
            <li>Pedir a exclusão dos dados tratados com base no seu consentimento.</li>
            <li>Pedir a portabilidade e saber com quem compartilhamos.</li>
            <li>
              <b>Retirar a autorização</b> — e ser informado do que acontece se retirar: a
              conversa termina e as indicações param, sem efeito nenhum sobre sua inscrição no
              evento.
            </li>
            <li>
              Opor-se a um tratamento e pedir revisão de decisão automatizada. As sugestões de
              conexão são geradas por inteligência artificial; se quiser, um humano revisa.
            </li>
          </ul>
        </Secao>

        <Secao titulo="Como parar de receber">
          <p>
            Responda <b>SAIR</b> na conversa do WhatsApp. É imediato, é gratuito e vale para
            todos os eventos daquela organizadora — não só para o que enviou a mensagem.
          </p>
          <p>
            Para voltar depois, é preciso autorizar de novo. Não há um botão de reativar, e essa
            ausência é deliberada: seria a forma de desfazer um pedido de saída sem parecer que
            se está desfazendo.
          </p>
        </Secao>

        <Secao titulo="Contato">
          <p>
            Para exercer qualquer um dos direitos acima, ou para falar com o encarregado pelo
            tratamento de dados:
          </p>
          <p style={{ fontSize: 15 }}>
            <a href={`mailto:${EMAIL_ENCARREGADO}`} style={{ color: 'var(--primary)' }}>
              {EMAIL_ENCARREGADO}
            </a>
          </p>
          <p>
            Respondemos em até 15 dias. Você também pode escrever diretamente na conversa do
            WhatsApp — um atendente humano lê.
          </p>
          <p>
            Se não ficar satisfeito, você pode apresentar reclamação à Autoridade Nacional de
            Proteção de Dados (ANPD).
          </p>
        </Secao>

        <Secao titulo="Mudanças neste aviso">
          <p>
            Se mudarmos algo relevante — uma finalidade nova, um compartilhamento novo —
            pediremos sua autorização outra vez. A data no topo indica a versão vigente, e o
            texto que vale para você é o que estava no ar no dia em que você autorizou. É por
            isso que guardamos esse texto junto com o seu consentimento.
          </p>
        </Secao>
      </div>
    </div>
  );
}

const LISTA: CSSProperties = {
  margin: '10px 0',
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 10px', color: 'var(--text)' }}>
        {titulo}
      </h2>
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: 'var(--text-muted)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {children}
      </div>
    </section>
  );
}

/** A abertura, com um pouco mais de peso que o corpo das seções. */
function Bloco({ children }: { children: ReactNode }) {
  return (
    <div
      className="card"
      style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-muted)', marginBottom: 32 }}
    >
      {children}
    </div>
  );
}
